import apiClient from '../api-client';
import type { Appointment } from '@geely/shared';
import type { AxiosError } from 'axios';

export interface AvailabilitySlot {
  time: string;
  available: number;
  total: number;
}

export interface AvailabilityResponse {
  storeId: string;
  date: string;
  slots: AvailabilitySlot[];
}

export interface ConflictErrorResponse {
  error: string;
  code: string;
  data?: {
    alternativeSlots?: Array<{
      date: string;
      time: string;
      availableCount: number;
    }>;
  };
}

export interface CreateAppointmentRequest {
  storeId: string;
  date: string;
  time: string;
  customerName: string;
  customerPhone: string;
  customerEmail: string;
}

export interface UpdateAppointmentRequest {
  status?: string;
  storeId?: string;
  date?: string;
  time?: string;
  customerName?: string;
  customerPhone?: string;
  customerEmail?: string;
}

export const appointmentsApi = {
  getById: (id: string) =>
    apiClient.get<Appointment>(`/appointments/${id}`),

  getFullById: (id: string) =>
    apiClient.get<Appointment>(`/appointments/${id}/details`),

  create: (data: CreateAppointmentRequest) =>
    apiClient.post<Appointment>('/appointments', data),

  update: (id: string, data: UpdateAppointmentRequest) =>
    apiClient.patch<Appointment>(`/appointments/${id}`, data),

  generateGuestToken: (id: string, email: string) =>
    apiClient.post<{ token: string; expiresAt: string }>(
      `/appointments/${id}/guest-token`,
      { customerEmail: email }
    ),

  getAvailability: (storeId: string, date: string) =>
    apiClient.get<AvailabilityResponse>('/appointments/availability', {
      params: { storeId, date },
    }),

  // CAMADA 3: RACE CONDITION PROTECTION - JIT Availability Check
  // This function is called IMMEDIATELY before submitting a booking
  // to re-validate that the slot is still available (last-second check).
  // If the slot was taken between selection and submit, we catch it here.
  checkAvailability: async (storeId: string, date: string, time: string) => {
    const response = await apiClient.get<AvailabilityResponse>('/appointments/availability', {
      params: { storeId, date },
    });

    const slot = response.data.slots.find((s) => s.time === time);
    if (!slot) {
      return { available: false, remaining: 0 };
    }

    return {
      available: slot.available > 0,
      remaining: slot.available,
    };
  },

  confirmPresence: (token: string) =>
    apiClient.post<{
      alreadyConfirmed: boolean;
      appointment: {
        id: string;
        date: string;
        time: string;
        customerName: string;
        storeId: string;
        status: string;
      };
      message: string;
    }>('/appointments/confirm', { token }),

  recoverAppointmentId: (data: { customerEmail: string; customerCpf: string }) =>
    apiClient.post<{ message: string }>('/appointments/recover', data),
};
